import { sumAmounts } from '@bg-shared/utils/sumAmounts';
import { IGameOptions } from '@bg-shared/business/GamesOptions';
import { optionIDConverter } from '@bg-shared/business/CasinoBetting/utils/optionIDConverter';
import { IBettingLimitsResponseFormatted } from '@bg-shared/business/BettingLimits/interfaces';
import {
    ICasinoAutoPlay,
    ICasinoRecentActionsModel,
    IChipAction,
    IChipAmounts,
} from './interfaces';

export const casinoBettingSelectors = {
    getRecentWin: (state: ICasinoRecentActionsModel) => state.recentWin,

    filterBetsToRepeat: (
        recentActions: ICasinoRecentActionsModel,
        gameOptions?: IGameOptions,
        activeOptionsClasses: string[] = [],
    ): ICasinoRecentActionsModel['recentBets'] => {
        if (!recentActions.recentBets?.length) {
            return [];
        }

        return !activeOptionsClasses.length
            ? recentActions.recentBets
            : recentActions.recentBets.filter(
                  (bet) =>
                      activeOptionsClasses.includes(bet.oddClass) &&
                      !!gameOptions?.options[bet.oddId]?.value,
              );
    },

    hasBetsToRepeat: (
        recentActions: ICasinoRecentActionsModel,
        gameOptions?: IGameOptions,
        activeOptionsClasses: string[] = [],
    ): boolean =>
        !!casinoBettingSelectors.filterBetsToRepeat(
            recentActions,
            gameOptions,
            activeOptionsClasses,
        ).length,

    getAllBetsByRunId:
        (runId: ICasinoRecentActionsModel['runId']) => (state: ICasinoRecentActionsModel) => {
            if (state.currentBetting.runId === runId) {
                return state.currentBetting.bets;
            }

            if (state.runId === runId) {
                return state.recentBets;
            }

            return [];
        },

    getRecentBetsByRunId:
        (runId: ICasinoRecentActionsModel['runId']) =>
        (state: ICasinoRecentActionsModel): ICasinoRecentActionsModel['recentBets'] =>
            state.runId === runId ? state.recentBets : [],

    hasActions: (state: IChipAction[][]) => !!state.length,

    getLastAction: (state: IChipAction[][]): IChipAction => {
        const filteredState = state.filter((action) => !action[0].pendingUndo);
        return filteredState[filteredState.length - 1]?.[0];
    },

    hasStagedBets: (state: IChipAmounts) => Object.values(state).some((value) => value > 0),

    getLimitsByOptionId: (state: IBettingLimitsResponseFormatted) => state?.validLimitsByOptionId,

    getTotalBetAmount: (state: IChipAmounts | number[]): number =>
        sumAmounts(Array.isArray(state) ? state : Object.values(state)),

    getTotalBetAmountByRunId:
        (runId: ICasinoRecentActionsModel['runId']) =>
        (state: ICasinoRecentActionsModel): number =>
            sumAmounts(
                casinoBettingSelectors
                    .getRecentBetsByRunId(runId)(state)
                    .map((recentBet) => recentBet.amount),
            ),

    hasChips: (state: IChipAmounts): boolean => !!Object.values(state).length,

    getAutoPlayBetIds: (state: ICasinoAutoPlay): number[] => Object.keys(state.betInfo).map(Number),

    filterChipsByOptionsClasses:
        (gameOptions: IGameOptions, optionsClasses: string[] = []) =>
        (state: IChipAmounts): IChipAmounts => {
            if (!optionsClasses?.length) {
                return state;
            }

            return Object.entries(state).reduce<IChipAmounts>((acc, [optionItemID, amount]) => {
                const { oddId } = optionIDConverter.parse(optionItemID);
                if (
                    gameOptions.options[oddId] &&
                    optionsClasses.includes(gameOptions.options[oddId].optionClass)
                ) {
                    acc[optionItemID] = amount;
                }

                return acc;
            }, {});
        },
};
